import { CSSInterpolation } from '@emotion/css/create-instance'
import React from 'react'

import { Checkbox } from './Checkbox'

type CheckboxListOption<T extends string> = {
  /**
   * Label text shown next to this checkbox.
   */
  label: string

  /**
   * The value of this checkbox. This checkbox is considered selected when
   * its value is listed in the `value` array of its parent CheckboxList.
   * This value should be unique among all of the passed in CheckboxListOptions.
   */
  value: T

  /**
   * The state of the checkbox. "disabled" marks this option as disabled.
   */
  state?: 'default' | 'disabled'
}

type Props<T extends string> = {
  /**
   * An array of the currently selected option values.
   */
  values: readonly T[]

  /**
   * An array describing each of the checkbox options that the user can choose from.
   */
  options: readonly CheckboxListOption<T>[]

  /**
   * Called when the user selects or deselects one of the passed in `options`.
   */
  onChange: ({ values }: { values: T[] }) => void

  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

export function DO_NOT_USE_CheckboxListOptionJustForPropsDocs<T extends string>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  props: CheckboxListOption<T>
) {
  return null
}

/**
 * Renders a list of checkboxes, allowing a user to select multiple items
 * from a list of options.
 */
export function CheckboxList<T extends string>({
  onChange,
  options,
  overrideRootStyles,
  values,
}: Props<T>) {
  return (
    <div css={overrideRootStyles}>
      {options.map(option => (
        <Checkbox
          key={option.value}
          isChecked={values.includes(option.value)}
          label={option.label}
          onChange={({ isChecked }) => {
            const valuesSet = new Set(values)
            if (isChecked) {
              valuesSet.add(option.value)
            } else {
              valuesSet.delete(option.value)
            }
            onChange({ values: Array.from(valuesSet) })
          }}
          state={option.state}
          value={option.value}
        />
      ))}
    </div>
  )
}
